/**
 * Dark Mode Toggle
 * Ported from Type-on-Strap assets/js/partials/dark-mode.js
 *
 * Two colour palettes defined via CSS custom properties on html[data-theme]:
 *   - 'light' (default)
 *   - 'dark'
 *
 * The variable isAutoTheme is defined in the inline <head> script
 * (default.liquid) from site config.
 */

/* global isAutoTheme */

/**
 * Get the stored theme mode.
 * Returns "auto", "light", "dark", or null (initial auto, never clicked).
 */
function currentMode() {
  return localStorage.getItem("theme-mode");
}

/**
 * Resolve what the system preference is right now.
 */
function systemTheme() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Resolve the effective theme (light/dark) for a given mode.
 * null or "auto" follows system preference; "light"/"dark" are explicit.
 */
function resolveTheme(mode) {
  if (!mode || mode === "auto") {
    return systemTheme();
  }
  return mode;
}

/**
 * Update the toggle icon to reflect the current mode.
 * null (initial auto) shows the auto icon.
 */
function updateIcon(mode) {
  const iconAuto = document.getElementById("theme-icon-auto");
  const iconLight = document.getElementById("theme-icon-light");
  const iconDark = document.getElementById("theme-icon-dark");
  if (!iconAuto) return;

  iconAuto.style.display = (!mode || mode === "auto") ? "" : "none";
  iconLight.style.display = mode === "light" ? "" : "none";
  iconDark.style.display = mode === "dark" ? "" : "none";
}

/**
 * Apply theme to the document and sync UI.
 * Pass null to apply without storing (initial auto).
 */
function applyMode(mode) {
  const theme = resolveTheme(mode);
  document.documentElement.setAttribute("data-theme", theme);
  if (mode !== null) {
    localStorage.setItem("theme-mode", mode);
  }
  updateIcon(mode);
  syncCommentTheme(theme);
}

/**
 * Determine the next mode in the toggle cycle.
 *
 * First click from auto confirms the current system preference explicitly,
 * second click flips to the opposite, third returns to auto:
 *
 *   day  (auto == light) → auto → light → dark → auto
 *   night (auto == dark)  → auto → dark → light → auto
 */
function nextMode(mode) {
  const sys = systemTheme();
  const cycle = sys === "light"
    ? ["auto", "light", "dark"]
    : ["auto", "dark", "light"];

  const current = mode || "auto";
  const idx = cycle.indexOf(current);
  return cycle[(idx + 1) % cycle.length];
}

export function themeToggle() {
  applyMode(nextMode(currentMode()));
}

/**
 * Initialize theme on page load.
 */
export function bootstrapTheme() {
  if (isAutoTheme) {
    // Migrate from old "theme" key if present
    const legacy = localStorage.getItem("theme");
    if (legacy && !currentMode()) {
      localStorage.setItem("theme-mode", legacy);
      localStorage.removeItem("theme");
    }

    const mode = currentMode();
    applyMode(mode); // null on first visit — shows auto icon, no storage

    // Listen for system preference changes when in auto mode
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
      const m = currentMode();
      if (!m || m === "auto") {
        applyMode(m);
      }
    });
  }
}

/**
 * Sync comment widget theme with current dark/light mode.
 * Sends postMessage to Giscus and Utterances iframes.
 * Cusdis reads data-theme on load (no postMessage support).
 * Disqus does not support dynamic theme switching.
 */
function syncCommentTheme(theme) {
  // Giscus
  const giscusFrame = document.querySelector("iframe.giscus-frame");
  if (giscusFrame) {
    giscusFrame.contentWindow.postMessage(
      { giscus: { setConfig: { theme: theme === "dark" ? "dark" : "light" } } },
      "https://giscus.app"
    );
  }

  // Utterances
  const utterancesFrame = document.querySelector("iframe.utterances-frame");
  if (utterancesFrame) {
    utterancesFrame.contentWindow.postMessage(
      { type: "set-theme", theme: theme === "dark" ? "github-dark" : "github-light" },
      "https://utteranc.es"
    );
  }
}
